
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { AppKey, AppOffer } from '@run-diffusion/shared';
import ErrorState from '@/components/states/ErrorState.vue';
import EmptyState from '@/components/states/EmptyState.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import AppOfferCard from '@/views/Sessions/AppOffersList/AppOfferCard/AppOfferCard.vue';
import { ROUTER } from '@/router/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { getDomainClaimRestrictionMessage } from '@/utils/domainClaimRestriction';

export default Vue.extend({
	name: 'AppOffersList',
	props: {
		selectionMode: { type: Boolean, default: false },
	},
	data () {
		return {
			loading: false, // TODO: loading state with Vuex
			hasError: false, // TODO: error state with Vuex
			intersectAppKey: null,
			canSetIntersectAppOffer: false,

			resourcesDialog: {
				open: false,
				appOffer: null,
			},
		};
	},
	computed: {
		...mapState([
			'team',
			'user',
			'appOffers',
			'deploymentImages',
			'loadingUser',
			'loadingTeam',
		]),
		canUseMobius (): boolean {
			return !!this.user;
		},
		mobiusSupportedAppKeys (): Record<AppKey, true> {
			// Derived from the live `deploymentImages` collection — an app is "Mobius-supported"
			// iff at least one deployment image targeting that app exists and is visible to the user.
			const out: Record<AppKey, true> = {} as Record<AppKey, true>;
			for (const d of (this.deploymentImages || [])) {
				if (d && d.app) out[d.app] = true;
			}
			return out;
		},
		...mapGetters([
			'activeDomainClaimRestriction',
			'displayLabels',
		]),
		sessionBlockedByDomainClaim (): boolean {
			if (this.selectionMode) return false;
			return !!this.activeDomainClaimRestriction;
		},
		domainClaimSelectTooltip (): string {
			if (!this.activeDomainClaimRestriction) return '';
			const base = getDomainClaimRestrictionMessage(this.activeDomainClaimRestriction, this.displayLabels.team);
			return `${base} Switch to the correct ${this.displayLabels.team} account in the toolbar (or update your sign-in email) to launch open-source apps here.`;
		},
	},
	watch: {
		appOffers (newVal: AppOffer[], oldVal: AppOffer[]) {
			if (!this.intersectAppKey && newVal && newVal.length && !(oldVal && oldVal.length)) {
				this.intersectAppKey = newVal[0].app;
				setTimeout(() => {
					this.canSetIntersectAppOffer = true;
				}, 500);
			}
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		scrollToAppOffer (app: AppKey) {
			this.$scrollTo(`#app-offer-${app}`, 500, {
				offset: -50,
			});
		},
		isMobiusSupported (appOffer: AppOffer): boolean {
			if (!this.canUseMobius) return false;
			return !!(appOffer && this.mobiusSupportedAppKeys[appOffer.app]);
		},
		onLearnMoreMobius () {
			window.open('https://rundiffusion.com/mobius', '_blank');
		},
		onAppOfferCardIntersect (appOffer: AppOffer) {
			if (appOffer && this.canSetIntersectAppOffer) {
				this.intersectAppKey = appOffer.app;
			}
		},
		onAppOfferSelect (appOffer: AppOffer) {
			if (this.sessionBlockedByDomainClaim) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: this.domainClaimSelectTooltip,
					show: true,
				});
				return;
			}
			if (this.selectionMode) {
				this.$emit('on-app-offer-select', appOffer);
			} else {
				this.routeToSetupSession(appOffer.app);
			}
		},
		routeToSetupSession (app: AppKey) {
			if (this.$route.name === ROUTER.ANOTHER_SESSION) {
				this.routerPush(this.$route, this.$router, { name: ROUTER.SETUP_ANOTHER_SESSION, query: { app } });
			} else {
				this.routerPush(this.$route, this.$router, { name: ROUTER.SETUP_SESSION, query: { app } });
			}
		},
	},
	components: {
		AppOfferCard,
		EmptyState,
		LoadingState,
		ErrorState,
	},
});
